import { ChangeDetectionStrategy, Component, inject, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, map, Observable, withLatestFrom } from 'rxjs';
import { isFormChanged } from 'src/app/common/guards/save-data-guard/form-value.service';
import { MessageLevel, UpdateCategoryDialog } from 'src/app/interfaces';
import { NotificationState } from 'src/app/notifications/notifications.state';
import { Category } from 'src/app/swagger-api';
import { MaybeNull } from 'src/app/utils';
import { DialogActionTypes } from '../dialogs.enum';

export const CATEGORY_UPDATION_CONTEXT = new InjectionToken<UpdateCategoryDialog>(`CATEGORY_UPDATION_CONTEXT`);

type UpdateCategoryFormGroup = FormGroup<{
  categoryTitle: FormControl<string>;
  parentId: FormControl<MaybeNull<string>>;
}>;

@Component({
  selector: 'app-dialog-update-category',
  templateUrl: './dialog-update-category.component.html',
  styleUrls: ['./dialog-update-category.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogUpdateCategoryComponent extends NotificationState {
  protected notificationLevel: MessageLevel = 'INFO';

  public readonly context = inject(CATEGORY_UPDATION_CONTEXT);

  public onParentCategoryInput(_event: Event): void {
    const formControl = this.form.get('parentId');

    // Set the parent id as null when the text is changed, it should only be set when a category is selected
    formControl.setValue(null);
    formControl.markAsTouched();
  }

  public onParentCategoryInputBlur(): void {
    const formControl = this.form.get('parentId');
    formControl.markAsTouched();
  }

  public trimTitle(): void {
    const formControl = this.form.get('categoryTitle');

    const previousValue = formControl.value;

    formControl.setValue(previousValue.trim());
    formControl.markAsTouched();
  }

  public onKeyDown(): void {
    const formControl = this.form.get('categoryTitle');

    formControl.markAsTouched();
  }

  public get category(): Category {
    return this.context.category;
  }

  public get isSubcategory(): boolean {
    return this.category.parent_id !== null && this.category.parent_id !== undefined;
  }

  public form: UpdateCategoryFormGroup = new FormGroup({
    parentId: this.isSubcategory
      ? new FormControl<MaybeNull<string>>(this.category.parent_id, Validators.required)
      : new FormControl<string>(''),
    categoryTitle: new FormControl<string>(this.category.title, [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(200),
    ]),
  });

  public readonly hasUnsavedChanges$ = isFormChanged(this.form);

  public actionsHandler(action: DialogActionTypes): void {
    this.dialogActions[action]();
  }

  public submit(): void {
    this.confirmAction();
  }

  public override confirmAction(): void {
    const parentId: string = this.form.controls.parentId.value;
    const categoryTitle: string = this.form.controls.categoryTitle.value;

    this.context
      .update(this.category.entity_id, categoryTitle, parentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.activeModal.close(true);
      });
  }

  public get isValid(): boolean {
    return this.form.valid;
  }

  public onSelectParentCategory(event: NgbTypeaheadSelectItemEvent<Category>): void {
    const selectedCategory = event.item;
    this.form.get('parentId').setValue(selectedCategory.entity_id);
  }

  public formatParentCategory = (category: Category): string => {
    return category.title;
  };

  public searchParentCategories = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      withLatestFrom(this.context.existedCategories),
      map(([term, existedCategories]) =>
        term === ''
          ? existedCategories
          : existedCategories
              .filter((category) => category.title.toLowerCase().includes(term.toLowerCase()))
              .slice(0, 10),
      ),
    );
}
